import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "tab",
    "editableCell",
    "tableContainer",
    "removeAction",
    "editButton",
    "continueButton",
    "addPortcoModal",
    "portfolioCompanySelect",
    "addRowTr",
    "existingCompanyForm",
    "newCompanyForm",
    "newCompanyLegalName",
    "newCompanyDisplayName",
    "tabErrors",
  ]
  static values = {
    editMode: Boolean,
    editingTab: String,
  }

  updateTable() {
    const data = {
      sort: this.currentSort,
      dir: this.currentSortDirection,
    }

    $.get(window.location.href, data, (response) => {
      const tempDiv = document.createElement("div")
      tempDiv.innerHTML = response

      // Update all tab contents
      const tabs = [
        "summary",
        "portfolio-companies",
        "financials-statements",
        "distribution-allocation",
      ]
      tabs.forEach((tabId) => {
        const newTable = tempDiv.querySelector(
          `#${tabId} .b-manage-status-table-container`,
        )
        const currentTable = document.querySelector(
          `#${tabId} .b-manage-status-table-container`,
        )
        if (newTable && currentTable) {
          currentTable.innerHTML = newTable.innerHTML
        }
      })
    })
  }

  connect() {
    // Check URL params for active tab
    const urlParams = new URLSearchParams(window.location.search)
    const activeTab = urlParams.get("tab") || "summary"
    this.activateTab(activeTab)

    // Initialize sorting
    this.currentSort = this.element.dataset.sort || "company"
    this.currentSortDirection = this.element.dataset.sortDir || "asc"

    // Initialize edit button visibility
    if (!this.editModeValue) {
      this.editButtonTarget.style.display = [
        "portfolio-companies",
        "distribution-allocation",
        "financials-statements",
      ].includes(activeTab)
        ? "inline-block"
        : "none"
    }

    // Continue button styling is now handled in the view

    // Initialize realized checkboxes on page load
    this.initializeRealizedCheckboxes()
  }

  initializeRealizedCheckboxes() {
    // Only initialize checkboxes in the distribution-allocation tab
    const distributionTab = document.querySelector("#distribution-allocation")
    if (!distributionTab) return

    distributionTab
      .querySelectorAll(".realized-checkbox")
      .forEach((checkbox) => {
        const isRealized = checkbox.dataset.realized === "true"
        if (isRealized) {
          checkbox.classList.add("realized-checkbox--checked")

          // Also disable other checkboxes in the same row and add grey background
          const row = checkbox.closest("tr")
          if (row) {
            row.querySelectorAll(".realized-checkbox").forEach((cb) => {
              if (cb !== checkbox) {
                cb.classList.add("realized-checkbox--disabled")
              }
            })

            // Add grey background to cells before the realized quarter
            const cells = Array.from(row.querySelectorAll("td"))
            const currentCell = checkbox.closest("td")
            const currentCellIndex = cells.indexOf(currentCell)

            cells.forEach((cell, index) => {
              if (index > 0 && index < currentCellIndex) {
                cell.classList.add("realized-future-cell")
              }
            })
          }
        }
      })
  }

  onSortTable(e) {
    // Prevent sorting when in edit mode
    if (this.editModeValue) {
      return
    }

    const sort = e.currentTarget.dataset.sort

    if (this.currentSort == sort) {
      this.currentSortDirection =
        this.currentSortDirection == "asc" ? "desc" : "asc"
    } else {
      this.currentSortDirection = "asc"
      this.currentSort = sort
    }

    this.updateTable()
  }

  switchTab(event) {
    const tabId = event.currentTarget.dataset.tabId
    this.activateTab(tabId)

    // Update URL without page reload
    const url = new URL(window.location)
    url.searchParams.set("tab", tabId)
    window.history.pushState({}, "", url)

    // Show/hide edit button based on tab
    if (!this.editModeValue) {
      this.editButtonTarget.style.display = [
        "portfolio-companies",
        "distribution-allocation",
        "financials-statements",
      ].includes(tabId)
        ? "inline-block"
        : "none"
    }
  }

  activateTab(tabId) {
    // Remove active class from all tabs and panes
    document.querySelectorAll(".nav-link").forEach((tab) => {
      tab.classList.remove("active")
    })
    document.querySelectorAll(".tab-pane").forEach((pane) => {
      pane.classList.remove("show", "active")
    })

    // Activate selected tab and pane
    const selectedTab = document.querySelector(`[data-tab-id="${tabId}"]`)
    const selectedPane = document.querySelector(`#${tabId}`)

    if (selectedTab && selectedPane) {
      selectedTab.classList.add("active")
      selectedPane.classList.add("show", "active")
    }
  }

  toggleEdit(e) {
    e.preventDefault()

    // Get current active tab
    const activeTab = document.querySelector(".tab-pane.active")
    const activeTabId = activeTab.id

    // Toggle continue button visibility
    if (this.hasContinueButtonTarget) {
      this.continueButtonTarget.style.display = this.editModeValue
        ? "inline-block"
        : "none"
    }

    if (this.editModeValue) {
      // Save mode
      let payload = {
        snapshot_id: this.element.dataset.snapshotId,
        tab: this.editingTabValue,
      }

      switch (this.editingTabValue) {
        case "portfolio-companies":
          const companies = []
          const rows = document
            .querySelector("#portfolio-companies-table tbody")
            .querySelectorAll("tr:not(.totals-row):not(.d-none)")

          rows.forEach((row) => {
            const cells = row.querySelectorAll("td")
            if (cells.length >= 4) {
              const realizedCheckbox =
                cells[3]?.querySelector(".realized-checkbox")
              const isRealized =
                realizedCheckbox?.classList.contains(
                  "realized-checkbox--checked",
                ) || false
              companies.push({
                legal_name: cells[0].textContent.trim(),
                cost: parseFloat(
                  cells[1].querySelector("input")?.value ||
                    cells[1].textContent.trim(),
                ),
                fair_value: parseFloat(
                  cells[2].querySelector("input")?.value ||
                    cells[2].textContent.trim(),
                ),
                realized: isRealized,
              })
            }
          })
          payload.companies = companies
          break

        case "financials-statements":
          const financialsTable = document.querySelector(
            "#financials-statements table tbody",
          )
          payload.financials = {
            total_assets: parseFloat(
              financialsTable.querySelector(
                "tr:nth-child(1) td:nth-child(1) input",
              )?.value || "0",
            ),
            total_investments: parseFloat(
              financialsTable.querySelector(
                "tr:nth-child(2) td:nth-child(1) input",
              )?.value || "0",
            ),
            total_liabilities: parseFloat(
              financialsTable.querySelector(
                "tr:nth-child(3) td:nth-child(1) input",
              )?.value || "0",
            ),
            total_partners_capital: parseFloat(
              financialsTable.querySelector(
                "tr:nth-child(4) td:nth-child(1) input",
              )?.value || "0",
            ),
            year: parseInt(
              financialsTable.querySelector(
                "tr:nth-child(1) td:nth-child(3) input",
              )?.value || new Date().getFullYear().toString(),
            ),
            quarter:
              financialsTable.querySelector(
                "tr:nth-child(2) td:nth-child(3) select",
              )?.value ||
              (this.element.dataset.quarter
                ? `Q${this.element.dataset.quarter}`
                : "Q1"),
          }
          break

        case "distribution-allocation":
          const distributionRows = document
            .querySelector("#distribution-allocation tbody")
            .querySelectorAll(
              "tr:not(.totals-row):not(.statement-row):not(.d-none):not(.other-row)",
            )

          const distributions = []
          const otherBalanceSheetItems = []
          const realizations = []

          distributionRows.forEach((row) => {
            const cells = row.querySelectorAll("td")
            if (cells.length >= 2) {
              const legalName = cells[0].textContent.trim()
              const isOtherRow = legalName === "Other Balance Sheet Items/Fees"
              const portfolioCompanyId = isOtherRow
                ? null
                : row.dataset.portfolioCompanyId
              const investmentAssetId = row.dataset.investmentAssetId

              // Track if we found a realized checkbox for this row
              let realizedYear = null
              let realizedQuarter = null

              // Process each quarter cell (skip the first column which is the company name)
              for (let i = 1; i < cells.length; i++) {
                const inputGroup = cells[i].querySelector(".input-group")
                if (inputGroup) {
                  const input = inputGroup.querySelector("input")
                  const yearQuarter = inputGroup.dataset.yearQuarter
                  if (input && yearQuarter) {
                    const [year, quarter] = yearQuarter.split("-")
                    const amount = parseFloat(input.value || 0)

                    if (isOtherRow) {
                      otherBalanceSheetItems.push({
                        year: parseInt(year),
                        quarter: parseInt(quarter),
                        amount: amount,
                      })
                    } else {
                      distributions.push({
                        portfolio_company_id: portfolioCompanyId,
                        investment_asset_id: investmentAssetId,
                        year: parseInt(year),
                        quarter: parseInt(quarter),
                        amount: amount,
                      })
                    }

                    // Check if this cell has a realized checkbox that's checked
                    const realizedCheckbox =
                      inputGroup.querySelector(".realized-checkbox")
                    if (
                      realizedCheckbox &&
                      realizedCheckbox.classList.contains(
                        "realized-checkbox--checked",
                      )
                    ) {
                      realizedYear = parseInt(year)
                      realizedQuarter = parseInt(quarter)
                    }
                  }
                }
              }

              // If we found a realized checkbox for this row, add it to realizations
              if (realizedYear && realizedQuarter && !isOtherRow) {
                realizations.push({
                  portfolio_company_id: portfolioCompanyId,
                  investment_asset_id: investmentAssetId,
                  year: realizedYear,
                  quarter: realizedQuarter,
                  realized: true,
                })
              } else if (!isOtherRow && portfolioCompanyId) {
                // If no checked checkbox, send realized: false to clear any existing realization
                realizations.push({
                  portfolio_company_id: portfolioCompanyId,
                  investment_asset_id: investmentAssetId,
                  realized: false,
                })
              }
            }
          })

          // Also process the other row separately
          const otherRow = document.querySelector(
            "#distribution-allocation tbody .other-row",
          )
          if (otherRow) {
            const cells = otherRow.querySelectorAll("td")
            for (let i = 1; i < cells.length; i++) {
              const inputGroup = cells[i].querySelector(".input-group")
              if (inputGroup) {
                const input = inputGroup.querySelector("input")
                const yearQuarter = inputGroup.dataset.yearQuarter
                if (input && yearQuarter) {
                  const [year, quarter] = yearQuarter.split("-")
                  otherBalanceSheetItems.push({
                    year: parseInt(year),
                    quarter: parseInt(quarter),
                    amount: parseFloat(input.value || 0),
                  })
                }
              }
            }
          }

          payload.distributions = distributions
          payload.other_balance_sheet_items = otherBalanceSheetItems
          payload.realizations = realizations
          break
      }

      // Send payload to server
      fetch(
        `/manage/snapshots/${this.element.dataset.snapshotId}/update_statements`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify(payload),
        },
      )
        .then((response) => {
          if (response.ok) {
            // Reload the entire page to update warnings and button state
            window.location.href =
              window.location.pathname + "?tab=" + this.editingTabValue
          } else {
            return response.json().then((data) => {
              this.showErrors(data.errors || ["Failed to update statements"])
              // Stay in edit mode so user can fix the errors
            })
          }
        })
        .catch((error) => {
          console.error("Error updating statements:", error)
          this.showErrors(["An unexpected error occurred. Please try again."])
        })
    } else {
      // Edit mode
      this.clearErrors()
      this.editModeValue = true
      this.editingTabValue = activeTabId
      e.target.textContent = "Save Changes"
    }
  }

  editModeValueChanged() {
    // Toggle continue button visibility
    if (this.hasContinueButtonTarget) {
      this.continueButtonTarget.style.display = this.editModeValue
        ? "none"
        : "inline-block"
    }

    if (this.editModeValue) {
      // Only make cells editable in the current editing tab
      const editingPane = document.getElementById(this.editingTabValue)
      if (editingPane) {
        const editableCells = editingPane.querySelectorAll(
          '[data-snapshot-statements-review-target="editableCell"]',
        )
        editableCells.forEach((cell) => {
          const value = parseFloat(cell.textContent.replace(/[^0-9.-]+/g, ""))
          if (cell.dataset.type === "select") {
            const currentValue = cell.textContent.trim().toLowerCase()
            cell.innerHTML = `
              <select class="form-control">
                <option value="quarterly" ${
                  currentValue === "quarterly" ? "selected" : ""
                }>Quarterly</option>
                <option value="yearly" ${
                  currentValue === "yearly" ? "selected" : ""
                }>Yearly</option>
              </select>`
          } else if (cell.dataset.type === "number") {
            const value =
              parseInt(cell.textContent.trim()) || new Date().getFullYear()
            cell.innerHTML = `<input type="number" class="form-control" value="${value}">`
          } else if (cell.dataset.type === "select_quarter") {
            const currentValue = cell.textContent.trim()
            // Map numeric quarter to Q format if needed
            const mappedValue = currentValue.startsWith("Q")
              ? currentValue
              : `Q${currentValue}`

            cell.innerHTML = `
              <select class="form-control">
                <option value="Q1">Q1</option>
                <option value="Q2">Q2</option>
                <option value="Q3">Q3</option>
                <option value="Q4">Q4</option>
              </select>`

            const select = cell.querySelector("select")
            if (select) {
              select.value = mappedValue
            }
          } else {
            // Preserve the checkbox if it exists, maintaining its state
            const checkbox = cell.querySelector(".realized-checkbox")
            let checkboxHtml = ""
            if (checkbox) {
              // Preserve the checkbox's classes to maintain checked/disabled state
              checkboxHtml = checkbox.outerHTML
            }
            cell.innerHTML = `<div class="input-group-text">$</div><input type="number" step="0.01" class="form-control" value="${value}">${checkboxHtml}`
          }
        })

        // Show remove buttons and add row
        this.removeActionTargets.forEach((action) =>
          action.classList.remove("d-none"),
        )
        this.addRowTrTarget.classList.remove("d-none")

        // Enable realized checkbox divs in edit mode (only for distribution-allocation tab)
        if (this.editingTabValue === "distribution-allocation") {
          editingPane
            .querySelectorAll(".realized-checkbox")
            .forEach((checkbox) => {
              checkbox.style.cursor = "pointer"
              // Add event listener for realized checkbox clicks
              checkbox.addEventListener(
                "click",
                this.onRealizedCheckboxClick.bind(this),
              )
            })
        }

        // Italicize the current tab
        const currentTab = document.querySelector(
          `[data-tab-id="${this.editingTabValue}"]`,
        )
        if (currentTab) {
          currentTab.style.fontStyle = "italic"
        }

        // Hide sort icons in edit mode
        editingPane.querySelectorAll(".sortable img").forEach((icon) => {
          icon.style.display = "none"
        })
      }
    } else {
      // Revert all editable cells back to text
      this.editableCellTargets.forEach((cell) => {
        const input = cell.querySelector("input")
        const select = cell.querySelector("select")
        const checkbox = cell.querySelector(".realized-checkbox")
        let checkboxHtml = ""
        if (checkbox) {
          // Preserve the checkbox's classes to maintain checked/disabled state
          checkboxHtml = checkbox.outerHTML
        }

        if (input) {
          if (cell.dataset.type === "number") {
            cell.innerHTML = input.value + checkboxHtml
          } else {
            const value = parseFloat(input.value)
            cell.innerHTML =
              new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(value) + checkboxHtml
          }
        } else if (select) {
          cell.innerHTML = select.value + checkboxHtml
        }
      })

      // Hide remove buttons and add row
      this.removeActionTargets.forEach((action) =>
        action.classList.add("d-none"),
      )
      this.addRowTrTarget.classList.add("d-none")

      // Reset all tab styles and show sort icons
      document.querySelectorAll("[data-tab-id]").forEach((tab) => {
        tab.style.fontStyle = "normal"
      })
      document.querySelectorAll(".sortable img").forEach((icon) => {
        icon.style.display = ""
      })

      // Disable realized checkboxes when exiting edit mode
      document
        .querySelectorAll("input[type='checkbox']")
        .forEach((checkbox) => {
          checkbox.disabled = true
          // Remove event listener
          checkbox.removeEventListener(
            "change",
            this.onRealizedChange.bind(this),
          )
          // Clean up any realized display changes
          const row = checkbox.closest("tr")
          if (row) {
            const fairValueCell = row.querySelector("td:nth-child(3)")
            if (fairValueCell) {
              const inputGroup = fairValueCell.querySelector(".input-group")
              if (inputGroup) {
                const originalInput = inputGroup.querySelector(
                  "input:not(.realized-zero-input)",
                )
                const zeroInput = inputGroup.querySelector(
                  ".realized-zero-input",
                )

                if (originalInput) {
                  originalInput.style.display = "block"
                }
                if (zeroInput) {
                  zeroInput.style.display = "none"
                }
              }
            }
          }
        })
    }
  }

  onRealizedChange(event) {
    const checkbox = event.target
    this.updateFairValueForRealized(checkbox)
  }

  updateFairValueForRealized(checkbox) {
    // Find the fair value input in the same row
    const row = checkbox.closest("tr")
    if (!row) return

    const fairValueCell = row.querySelector("td:nth-child(3)")
    if (!fairValueCell) return

    const inputGroup = fairValueCell.querySelector(".input-group")
    if (!inputGroup) return

    const fairValueInput = inputGroup.querySelector("input")
    if (!fairValueInput) return

    if (checkbox.checked) {
      // Hide the original input and create a disabled zero input at the same level
      fairValueInput.style.display = "none"

      // Create or show the zero input within the input-group
      let zeroInput = inputGroup.querySelector(".realized-zero-input")
      if (!zeroInput) {
        zeroInput = document.createElement("input")
        zeroInput.type = "number"
        zeroInput.step = "0.01"
        zeroInput.className = "form-control realized-zero-input"
        zeroInput.value = "0"
        zeroInput.disabled = true
        zeroInput.style.backgroundColor = "#f8f9fa"
        inputGroup.appendChild(zeroInput)
      } else {
        zeroInput.style.display = "block"
      }
    } else {
      // Show the original input and hide the zero input
      fairValueInput.style.display = "block"

      // Restore the original fair value from data attribute
      const originalValue = inputGroup.dataset.originalFairValue
      if (originalValue && fairValueInput.value === "0") {
        fairValueInput.value = originalValue
      }

      const zeroInput = inputGroup.querySelector(".realized-zero-input")
      if (zeroInput) {
        zeroInput.style.display = "none"
      }
    }
  }

  removeRow(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("tr")
    if (row) {
      row.classList.add("d-none")
    }
  }

  onAddRow(e) {
    e.preventDefault()
    $(this.addPortcoModalTarget).modal("show")
  }

  togglePortcoForm(e) {
    const isExisting = e.target.value === "existing"
    this.existingCompanyFormTarget.classList.toggle("d-none", !isExisting)
    this.newCompanyFormTarget.classList.toggle("d-none", isExisting)
  }

  onAddPortcoConfirm(e) {
    e.preventDefault()

    const isExisting = document.querySelector("#existingCompanyRadio").checked

    if (isExisting) {
      const portfolioCompany =
        this.portfolioCompanySelectTarget.selectedOptions[0]
      if (!portfolioCompany) {
        alert("Please select a portfolio company")
        return
      }

      $.ajax({
        url:
          window.location.pathname + "/add_existing_company_statement_review",
        method: "POST",
        data: {
          company_name: portfolioCompany.text,
          portfolio_company_id: portfolioCompany.value,
        },
        success: () => {
          $(this.addPortcoModalTarget).modal("hide")
          window.location.reload()
        },
        error: () => {
          alert("Error mapping portfolio company")
        },
      })
    } else {
      const legalName = this.newCompanyLegalNameTarget.value.trim()
      const displayName = this.newCompanyDisplayNameTarget.value.trim()

      if (!legalName) {
        alert("Please fill in Legal Name")
        return
      }

      $.ajax({
        url: window.location.pathname + "/create_company_statement_review",
        method: "POST",
        data: {
          company_name: legalName,
          legal_name: legalName,
          display_name: displayName,
        },
        success: () => {
          $(this.addPortcoModalTarget).modal("hide")
          window.location.reload()
        },
        error: () => {
          alert("Error creating portfolio company")
        },
      })
    }
  }

  showErrors(errors) {
    if (this.hasTabErrorsTarget) {
      // Clear previous errors
      this.tabErrorsTarget.innerHTML = ""

      const errorList = document.createElement("ul")
      errors.forEach((error) => {
        const listItem = document.createElement("li")
        listItem.textContent = error
        errorList.appendChild(listItem)
      })

      this.tabErrorsTarget.appendChild(errorList)
      this.tabErrorsTarget.classList.remove("d-none")
    }
  }

  clearErrors() {
    if (this.hasTabErrorsTarget) {
      this.tabErrorsTarget.innerHTML = ""
      this.tabErrorsTarget.classList.add("d-none")
    }
  }

  onRealizedCheckboxClick(event) {
    if (!this.editModeValue) {
      return // Only allow clicks in edit mode
    }

    const checkbox = event.currentTarget
    const row = checkbox.closest("tr")
    const isCurrentlyChecked = checkbox.classList.contains(
      "realized-checkbox--checked",
    )

    if (isCurrentlyChecked) {
      // Unchecking - remove checked class and enable all checkboxes in this row
      checkbox.classList.remove("realized-checkbox--checked")

      // Enable all checkboxes in this row
      row.querySelectorAll(".realized-checkbox").forEach((cb) => {
        cb.classList.remove("realized-checkbox--disabled")
      })

      // Remove grey background from future cells
      row.querySelectorAll("td.realized-future-cell").forEach((cell) => {
        cell.classList.remove("realized-future-cell")
      })
    } else {
      // Check if this checkbox is disabled
      if (checkbox.classList.contains("realized-checkbox--disabled")) {
        return // Don't allow clicking disabled checkboxes
      }

      // Checking - first uncheck any other checkbox in this row
      row.querySelectorAll(".realized-checkbox").forEach((cb) => {
        cb.classList.remove("realized-checkbox--checked")
      })

      // Mark this checkbox as checked
      checkbox.classList.add("realized-checkbox--checked")

      // Disable all other checkboxes in this row
      row.querySelectorAll(".realized-checkbox").forEach((cb) => {
        if (cb !== checkbox) {
          cb.classList.add("realized-checkbox--disabled")
        }
      })

      // Add grey background to all future cells (cells after this one)
      const cells = Array.from(row.querySelectorAll("td"))
      const currentCell = checkbox.closest("td")
      const currentCellIndex = cells.indexOf(currentCell)

      cells.forEach((cell, index) => {
        if (index < currentCellIndex) {
          cell.classList.add("realized-future-cell")
        } else {
          cell.classList.remove("realized-future-cell")
        }
      })
    }
  }
}
